import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { truncateFileName } from 'Helpers/misc';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useApiRequest from 'Hooks/useApiRequest';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CloudArrowUpIcon from 'Assets/icons/cloud-arrow-up.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import FileTextIcon from 'Assets/icons/file-text.svg';
import KeyIcon from 'Assets/icons/key.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const STATE_OPEN = 'Open';
const STATE_COMPLETED = 'Completed';
const STATE_APPROVED = 'Approved';

const ACCEPTED_MIMES = 'application/pdf,.pdf,.doc,.docx,.xls,.xlsx';

// ---- Auth Modal ----

function PortalAuthModal({ sendCodeUrl, verifyUrl, onAuthenticated, initialCodePending }) {
    const [step, setStep] = useState(initialCodePending ? 'verify' : 'request'); // 'request' | 'verify'
    const [code, setCode] = useState('');
    const [error, setError] = useState('');

    const sendRequest = useApiRequest({ url: sendCodeUrl, displayErrorToast: false });
    const verifyRequest = useApiRequest({ url: verifyUrl, displayErrorToast: false });

    const handleSendCode = () => {
        setError('');
        sendRequest
            .post({})
            .then(() => {
                setStep('verify');
            })
            .catch((err) => {
                setError(err.response?.data?.message || 'Failed to send code. Please try again.');
            });
    };

    const handleVerify = () => {
        setError('');
        verifyRequest
            .post({ code })
            .then(() => {
                onAuthenticated();
            })
            .catch((err) => {
                setError(err.response?.data?.message || 'Invalid or expired code.');
            });
    };

    return (
        <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[60vh]">
            <div className="inbud-card tw-w-full tw-max-w-md">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-mb-6">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-blue/10 tw-flex tw-items-center tw-justify-center">
                        <KeyIcon className="tw-w-6 tw-h-6 tw-text-blue" />
                    </div>
                    <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{'Supplier Portal'}</h2>
                    <p className="tw-text-sm tw-text-gray-500 tw-text-center">
                        {step === 'request'
                            ? 'Click below to receive a one-time security code at your registered email address.'
                            : 'Enter the 6-character code sent to your email.'}
                    </p>
                </div>

                {error && <div className="tw-mb-4 tw-p-3 tw-rounded-lg tw-bg-red/10 tw-text-red tw-text-sm">{error}</div>}

                {step === 'request' ? (
                    <button className="button primary tw-w-full" onClick={handleSendCode} disabled={sendRequest.loading}>
                        {sendRequest.loading ? <LoadingSpinner size="sm" ySpacing={false} /> : 'Send Security Code'}
                    </button>
                ) : (
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <input
                            type="text"
                            maxLength={6}
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder={'Enter code'}
                            className="tw-w-full tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded-lg tw-px-4 tw-py-3 tw-text-gray-900 tw-text-center tw-text-xl tw-tracking-widest focus:tw-outline-none focus:tw-border-blue"
                        />
                        <button className="button primary tw-w-full" onClick={handleVerify} disabled={verifyRequest.loading || code.length !== 6}>
                            {verifyRequest.loading ? <LoadingSpinner size="sm" ySpacing={false} /> : 'Verify Code'}
                        </button>
                        <button
                            className="button secondary tw-w-full"
                            onClick={() => {
                                setStep('request');
                                setCode('');
                                setError('');
                            }}
                        >
                            {'Resend Code'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---- Portal Content ----

function PortalContent({ message, fields, submitUrl }) {
    return (
        <div className="tw-max-w-3xl tw-mx-auto tw-flex tw-flex-col tw-gap-6">
            {message && <div className="inbud-card tw-prose tw-prose-invert tw-max-w-none" dangerouslySetInnerHTML={{ __html: message }} />}
            <PortalFieldsTable fields={fields} submitUrl={submitUrl} />
        </div>
    );
}

// ---- Drop Zone ----

function DropZone({ onFiles, children }) {
    const [dragging, setDragging] = useState(false);
    const counter = useRef(0);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        counter.current++;
        setDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        counter.current--;
        if (counter.current === 0) setDragging(false);
    }, []);

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            counter.current = 0;
            setDragging(false);
            const dropped = Array.from(e.dataTransfer.files);
            if (dropped.length > 0) onFiles(dropped);
        },
        [onFiles],
    );

    return (
        <div
            onDragEnter={handleDragEnter}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-text-center tw-transition-colors tw-cursor-pointer ${
                dragging ? 'tw-border-blue-400 tw-bg-blue-400/10' : 'tw-border-gray-300 hover:tw-border-gray-400'
            }`}
        >
            {children}
        </div>
    );
}

// ---- Field Upload Row ----

function FieldUploadRow({ field, submitUrl }) {
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [submitted, setSubmitted] = useState(field.state !== STATE_OPEN);
    const [errors, setErrors] = useState({});

    const request = useApiRequest({
        url: submitUrl,
        options: { headers: { 'Content-Type': 'multipart/form-data' } },
        displayErrorToast: false,
    });

    const addFiles = useCallback((newFiles) => {
        setFiles((prev) => [...prev, ...newFiles]);
        setErrors({});
    }, []);

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files);
        addFiles(selected);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        if (files.length === 0) {
            setErrors({ files: 'At least one file is required' });
            return;
        }

        const formData = new FormData();
        formData.append('field_id', field.id);
        files.forEach((f) => formData.append('files[]', f));

        request
            .post(formData)
            .then(() => {
                setSubmitted(true);
                setFiles([]);
            })
            .catch((err) => {
                if (err.response?.data?.errors) {
                    setErrors(err.response.data.errors);
                } else {
                    setErrors({ files: err.response?.data?.message || 'An error occurred, please try again' });
                }
            });
    };

    const currentState = submitted && field.state === STATE_OPEN ? STATE_COMPLETED : field.state;

    const stateLabel = () => {
        switch (currentState) {
            case STATE_COMPLETED:
                return <span className="tw-text-green tw-text-xs tw-font-semibold">{'Completed'}</span>;
            case STATE_APPROVED:
                return <span className="tw-text-blue tw-text-xs tw-font-semibold">{'Approved'}</span>;
            default:
                return <span className="tw-text-yellow-400 tw-text-xs tw-font-semibold">{'Open'}</span>;
        }
    };

    const firstError = errors.files || errors['files.0'] || Object.values(errors)[0];

    return (
        <div className="inbud-card tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                <div>
                    <h3 className="tw-text-base tw-font-semibold tw-text-gray-900">{field.name}</h3>
                    {field.instruction && <p className="tw-text-sm tw-text-gray-500 tw-mt-1">{field.instruction}</p>}
                </div>
                <div className="tw-shrink-0">{stateLabel()}</div>
            </div>

            {field.admin_attachments && field.admin_attachments.length > 0 && (
                <div>
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-2 tw-uppercase tw-tracking-wider">{'Documents to download'}</p>
                    <div className="tw-space-y-2">
                        {field.admin_attachments.map((a) => (
                            <a
                                key={a.id}
                                href={a.download_url}
                                className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-border-gray-200 hover:tw-border-blue-400 tw-transition-colors tw-group"
                            >
                                <DownloadIcon className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0 group-hover:tw-text-blue-400" />
                                <span className="tw-text-sm tw-text-gray-600 group-hover:tw-text-blue-400 tw-truncate">{a.name}</span>
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {submitted ? (
                <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-green/10">
                    <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green tw-shrink-0" />
                    <p className="tw-text-sm tw-text-green">{'Documents submitted successfully.'}</p>
                </div>
            ) : (
                <div>
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-2 tw-uppercase tw-tracking-wider">{'Upload your documents'}</p>
                    <DropZone onFiles={addFiles}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={ACCEPTED_MIMES}
                            onChange={handleFileChange}
                            className="tw-hidden"
                            id={`file-upload-${field.id}`}
                        />
                        <label htmlFor={`file-upload-${field.id}`} className="tw-cursor-pointer">
                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                <CloudArrowUpIcon className="tw-w-8 tw-h-8 tw-text-gray-400" />
                                <span className="tw-text-sm tw-text-gray-600">
                                    {'Drag and drop files here, or'} <span className="tw-text-blue-400 tw-font-medium">{'browse'}</span>
                                </span>
                                <span className="tw-text-xs tw-text-gray-400">{'Accepted formats: PDF, Word, Excel'}</span>
                            </div>
                        </label>
                    </DropZone>

                    {files.length > 0 && (
                        <div className="tw-mt-3 tw-space-y-2">
                            {files.map((file, index) => (
                                <div key={index} className="tw-flex tw-items-center tw-justify-between tw-p-2 tw-rounded-lg tw-bg-gray-50">
                                    <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                        <FileTextIcon className="tw-w-4 tw-h-4 tw-text-gray-500 tw-flex-shrink-0" />
                                        <span className="tw-text-sm tw-text-gray-700 tw-truncate">{truncateFileName(file.name)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="tw-flex-shrink-0 tw-ml-2 tw-p-1 tw-rounded hover:tw-bg-red/10 tw-transition-colors"
                                    >
                                        <TrashIcon className="tw-w-3.5 tw-h-3.5 tw-text-red" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {firstError && (
                        <div className="tw-mt-2 tw-text-sm tw-text-red">
                            <strong>{Array.isArray(firstError) ? firstError[0] : firstError}</strong>
                        </div>
                    )}

                    <div className="tw-flex tw-justify-end tw-mt-4">
                        <button type="button" className="button primary" disabled={request.loading || files.length === 0} onClick={handleSubmit}>
                            {request.loading ? <LoadingSpinner size="sm" ySpacing={false} /> : 'Save'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---- Portal Fields Table ----

function PortalFieldsTable({ fields, submitUrl }) {
    if (!fields || fields.length === 0) {
        return <div className="inbud-card tw-text-center tw-py-8 tw-text-gray-500">{'No documents required at this time.'}</div>;
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {fields.map((field) => (
                <FieldUploadRow key={field.id} field={field} submitUrl={submitUrl} />
            ))}
        </div>
    );
}

// ---- Main Component ----

export default function SupplierPortal({ portalData, initialAuthenticated, codePending, sendCodeUrl, verifyUrl }) {
    const [authenticated, setAuthenticated] = useState(initialAuthenticated);
    const [portal] = useState(portalData);

    if (!authenticated) {
        return (
            <PortalAuthModal sendCodeUrl={sendCodeUrl} verifyUrl={verifyUrl} initialCodePending={codePending} onAuthenticated={() => setAuthenticated(true)} />
        );
    }

    return <PortalContent message={portal.message} fields={portal.fields || []} submitUrl={portal.submit_url} />;
}

if (document.getElementById('supplierPortal')) {
    const rootEl = document.getElementById('supplierPortal');

    const portalData = JSON.parse(rootEl.getAttribute('data-portal') || '{}');
    const authenticated = rootEl.getAttribute('data-authenticated') === 'true';
    const codePending = rootEl.getAttribute('data-code-pending') === 'true';
    const sendCodeUrl = rootEl.getAttribute('data-send-code-url');
    const verifyUrl = rootEl.getAttribute('data-verify-url');

    ReactDOM.render(
        <App>
            <SupplierPortal
                portalData={portalData}
                initialAuthenticated={authenticated}
                codePending={codePending}
                sendCodeUrl={sendCodeUrl}
                verifyUrl={verifyUrl}
            />
        </App>,
        rootEl,
    );
}
